import { getConfig } from "@stryker/core/api/jobs";
import { QueryKeys } from "@stryker/core/queryKeys";
import { useQuery } from "@tanstack/react-query";
import { type FieldArrayWithId, type UseFieldArrayRemove, useFormContext } from "react-hook-form";
import DeleteIcon from "@/assets/icons/trash.svg?react";
import { Combobox } from "@/components/ui/combobox";
import { FormField, FormItem, FormMessage } from "@/components/ui/form";
import { Spinner } from "@/components/ui/spinner";
import { TextInput } from "@/components/ui/textInput";
import { Typography } from "@/components/ui/typography";
import { useQuoteItemOptions } from "@/hooks/useQuoteItemOptions";
import type { QuoteFormSchema } from "@/lib/schemas/createUpdateQuoteSchema";
import { cn } from "@/lib/utils";
import { getMargin } from "@/utils/calculations";

type MobileQuoteDetailsViewProps = {
  isFetchingInitialQuoteData: boolean;
  fields: FieldArrayWithId<QuoteFormSchema, "quoteDetails", "id">[];
  remove: UseFieldArrayRemove;
  canViewCostAndMargin: boolean;
  repairSystemId?: string | null;
};

export default function MobileQuoteDetailsView({
  isFetchingInitialQuoteData,
  fields,
  remove,
  canViewCostAndMargin,
  repairSystemId,
}: MobileQuoteDetailsViewProps) {
  const { register, control, watch, setValue } = useFormContext<QuoteFormSchema>();

  const quoteDetails = watch("quoteDetails");

  const { data: repairUnitTypes } = useQuery({
    queryKey: [QueryKeys.JobsConfig],
    queryFn: getConfig,
    staleTime: 1000000,
    select: (response) => response.repair_unit_types,
  });

  const { itemOptions, getUnitsForItem, isCustomItem } = useQuoteItemOptions(repairSystemId);

  function handleItemChange(index: number, value: string) {
    setValue(`quoteDetails.${index}.item_name`, value);

    if (isCustomItem(value)) {
      setValue(`quoteDetails.${index}.unit`, "");
      return;
    }

    const units = getUnitsForItem(value);
    const defaultUnit = units[0];
    if (defaultUnit) {
      applyUnit(index, defaultUnit);
    }
  }

  function handleUnitChange(index: number, itemName: string, unitValue: string) {
    const selectedUnit = getUnitsForItem(itemName).find((unit) => unit.unit === unitValue);
    if (selectedUnit) {
      applyUnit(index, selectedUnit);
    } else {
      setValue(`quoteDetails.${index}.unit`, unitValue);
    }
  }

  function applyUnit(index: number, unit: { unit: string; cost_per_unit: number; price_per_unit: number }) {
    setValue(`quoteDetails.${index}.unit`, unit.unit);
    setValue(`quoteDetails.${index}.price_per_unit`, unit.price_per_unit);
    if (canViewCostAndMargin) {
      setValue(`quoteDetails.${index}.cost_per_unit`, unit.cost_per_unit);
    }
  }

  return (
    <div className="space-y-4">
      {isFetchingInitialQuoteData && (
        <div className="flex justify-center rounded-md border border-gray-300 p-6">
          <Spinner />
        </div>
      )}
      {fields.map((field, index) => {
        const currentItemRow = quoteDetails[index];
        const currentItemName = currentItemRow.item_name;
        const currentItemUnit = currentItemRow.unit;
        const currentIsEditable = currentItemRow.is_editable;
        const currentCostPerUnit = currentItemRow.cost_per_unit ?? 0;
        const currentPricePerUnit = currentItemRow.price_per_unit ?? 0;
        const currentQuantity = currentItemRow.quantity ?? 0;
        const itemTotalCosts = currentQuantity * currentCostPerUnit;
        const itemTotalQuotes = currentQuantity * currentPricePerUnit;
        const margin = getMargin({ totalQuotes: itemTotalQuotes, totalCosts: itemTotalCosts });
        const itemIsCustom = isCustomItem(currentItemName);
        const availableUnits = itemIsCustom ? [] : getUnitsForItem(currentItemName);
        const showUnitDropdown = itemIsCustom || availableUnits.length > 1;
        const unitOptions = itemIsCustom
          ? (repairUnitTypes ?? []).map((unit) => ({ label: unit, value: unit }))
          : availableUnits.map((unit) => ({ label: unit.unit, value: unit.unit }));

        return (
          <div key={field.id} className="rounded-md border border-gray-300 bg-white p-4 shadow-sm">
            <div className="mb-4 flex items-start justify-between gap-3">
              <Typography className="font-semibold text-sm">Item #{index + 1}</Typography>
              <button
                type="button"
                onClick={() => remove(index)}
                className="cursor-pointer text-red-500"
                aria-label={`Remove item ${index + 1}`}
              >
                <DeleteIcon />
              </button>
            </div>

            <div className="flex flex-col gap-4">
              <div className="flex flex-col gap-2">
                <Typography className="text-gray-500 text-xs">Item</Typography>
                <FormField
                  control={control}
                  name={`quoteDetails.${index}.item_name`}
                  render={({ field }) => {
                    return (
                      <FormItem>
                        <Combobox
                          disabled={!currentIsEditable}
                          options={itemOptions}
                          value={field.value}
                          onValueChange={(value) => handleItemChange(index, value)}
                          placeholder="Item"
                          searchPlaceholder="Search items..."
                        />
                        <FormMessage />
                      </FormItem>
                    );
                  }}
                />
                {currentItemName === "Custom" && (
                  <TextInput {...register(`quoteDetails.${index}.custom_item_name`)} className="w-full" />
                )}
              </div>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="flex flex-col gap-2">
                  <Typography className="text-gray-500 text-xs">Quantity</Typography>
                  <TextInput
                    type="number"
                    {...register(`quoteDetails.${index}.quantity`, { valueAsNumber: true })}
                    className="w-full"
                  />
                </div>
                <div className="flex flex-col gap-2">
                  <Typography className="text-gray-500 text-xs">Unit</Typography>
                  {showUnitDropdown ? (
                    <FormField
                      key={currentItemUnit}
                      control={control}
                      name={`quoteDetails.${index}.unit`}
                      render={({ field }) => {
                        return (
                          <FormItem>
                            <Combobox
                              disabled={!currentIsEditable}
                              options={unitOptions}
                              value={field.value}
                              onValueChange={(value) => handleUnitChange(index, currentItemName, value)}
                              placeholder="Unit"
                              searchPlaceholder="Search units..."
                            />
                            <FormMessage />
                          </FormItem>
                        );
                      }}
                    />
                  ) : (
                    <TextInput value={currentItemUnit} disabled className="w-full" />
                  )}
                  {currentItemUnit === "CUSTOM" && (
                    <TextInput {...register(`quoteDetails.${index}.custom_unit`)} className="w-full" />
                  )}
                </div>
                {canViewCostAndMargin && (
                  <div className="flex flex-col gap-2">
                    <Typography className="text-gray-500 text-xs">Cost Per Unit</Typography>
                    <TextInput
                      type="number"
                      step={0.01}
                      {...register(`quoteDetails.${index}.cost_per_unit`, { valueAsNumber: true })}
                      className="w-full"
                    />
                  </div>
                )}
                <div className="flex flex-col gap-2">
                  <Typography className="text-gray-500 text-xs">Price Per Unit</Typography>
                  <TextInput
                    type="number"
                    step={0.01}
                    {...register(`quoteDetails.${index}.price_per_unit`, { valueAsNumber: true })}
                    className="w-full"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 gap-2 rounded-md bg-gray-50 p-3 text-sm">
                {canViewCostAndMargin && (
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-semibold">Total Cost</span>
                    <span>{Number.isNaN(itemTotalCosts) ? "N/A" : `$${itemTotalCosts.toFixed(2)}`}</span>
                  </div>
                )}
                <div className="flex items-center justify-between gap-2">
                  <span className="font-semibold">Total Revenue</span>
                  <span>{Number.isNaN(itemTotalQuotes) ? "N/A" : `$${itemTotalQuotes.toFixed(2)}`}</span>
                </div>
                {canViewCostAndMargin && (
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-semibold">Margin</span>
                    <span className={cn("rounded px-2 py-1", margin < 0 ? "bg-red-600 text-white" : "bg-green-300")}>
                      {margin.toFixed(2)}%
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
